import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Calendar, Clock, MapPin, Users, Plus, Edit, Trash2, Check, X,
  ArrowLeft, Loader2, Video, Phone, Building, Bell, CheckCircle, XCircle, HelpCircle
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { getErrorMessage } from '../utils/errorHandler';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APPOINTMENT_TYPES = [
  { value: 'meeting', label: 'Meeting', icon: Users },
  { value: 'counseling', label: 'Counseling', icon: HelpCircle },
  { value: 'class', label: 'Class', icon: Calendar },
  { value: 'prayer', label: 'Prayer', icon: Bell },
  { value: 'other', label: 'Other', icon: Calendar },
];

const LOCATION_TYPES = [
  { value: 'in_person', label: 'In Person', icon: Building },
  { value: 'virtual', label: 'Virtual', icon: Video },
  { value: 'phone', label: 'Phone Call', icon: Phone },
];

const STATUS_COLORS = {
  scheduled: 'bg-blue-500/20 text-blue-400',
  confirmed: 'bg-green-500/20 text-green-400',
  cancelled: 'bg-red-500/20 text-red-400',
  completed: 'bg-slate-500/20 text-slate-400',
};

const RESPONSE_ICONS = {
  accepted: { icon: CheckCircle, color: 'text-green-500' },
  declined: { icon: XCircle, color: 'text-red-500' },
  tentative: { icon: HelpCircle, color: 'text-yellow-500' },
};

const AppointmentsPage = () => {
  const { communityId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [myAppointments, setMyAppointments] = useState([]);
  const [members, setMembers] = useState([]);
  const [activeTab, setActiveTab] = useState('upcoming');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    appointment_type: 'meeting',
    date: '',
    start_time: '',
    end_time: '',
    duration_minutes: 60,
    location_type: 'in_person',
    location: '',
    invitees: [],
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    reminder_enabled: true,
    reminder_hours_before: 24,
    notes: ''
  });

  const fetchAppointments = useCallback(async () => {
    try {
      const [communityRes, myRes] = await Promise.all([
        communityId 
          ? axios.get(`${API}/appointments/communities/${communityId}/appointments`)
          : Promise.resolve({ data: { appointments: [] } }),
        axios.get(`${API}/appointments/my-appointments`)
      ]);
      
      setAppointments(communityRes.data.appointments || []);
      setMyAppointments(myRes.data.appointments || []);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    } finally {
      setLoading(false);
    }
  }, [communityId]);

  const fetchMembers = useCallback(async () => {
    if (!communityId) return;
    try {
      const response = await axios.get(`${API}/calendar/communities/${communityId}/assignable-members`);
      setMembers(response.data.members || []);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  }, [communityId]);

  useEffect(() => {
    fetchAppointments();
    fetchMembers();
  }, [fetchAppointments, fetchMembers]);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      appointment_type: 'meeting',
      date: '',
      start_time: '',
      end_time: '',
      duration_minutes: 60,
      location_type: 'in_person',
      location: '',
      invitees: [],
      guest_name: '',
      guest_email: '',
      guest_phone: '',
      reminder_enabled: true,
      reminder_hours_before: 24,
      notes: ''
    });
  };

  const handleCreate = async () => {
    if (!formData.title || !formData.date || !formData.start_time) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/appointments/communities/${communityId}/appointments`, formData);
      toast.success('Appointment created!');
      setCreateDialogOpen(false);
      resetForm();
      fetchAppointments();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create appointment'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (appointmentId) => {
    if (!confirm('Delete this appointment?')) return;
    
    try {
      await axios.delete(`${API}/appointments/communities/${communityId}/appointments/${appointmentId}`);
      toast.success('Appointment deleted');
      setViewDialogOpen(false);
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to delete appointment');
    }
  };

  const handleRespond = async (appointmentId, response) => {
    try {
      await axios.post(`${API}/appointments/communities/${communityId}/appointments/${appointmentId}/respond`, {
        response: response
      });
      toast.success(`Response: ${response}`);
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to respond');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const upcomingAppointments = [...appointments, ...myAppointments]
    .filter(a => a.status !== 'cancelled' && a.status !== 'completed')
    .filter((a, i, arr) => arr.findIndex(x => x.appointment_id === a.appointment_id) === i)
    .sort((a, b) => a.date.localeCompare(b.date));

  const pastAppointments = [...appointments, ...myAppointments]
    .filter(a => a.status === 'completed')
    .filter((a, i, arr) => arr.findIndex(x => x.appointment_id === a.appointment_id) === i)
    .sort((a, b) => b.date.localeCompare(a.date));

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-xl">{t('appointments')}</h1>
                <p className="text-sm text-slate-400">{t('manage_appointments')}</p>
              </div>
            </div>
          </div>
          <Button 
            onClick={() => setCreateDialogOpen(true)}
            className="bg-amber-500 hover:bg-amber-600 text-black gap-2"
          >
            <Plus className="w-4 h-4" />
            {t('new_appointment')}
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-900 border border-slate-800 mb-6">
            <TabsTrigger value="upcoming" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              {t('upcoming')} ({upcomingAppointments.length})
            </TabsTrigger>
            <TabsTrigger value="past" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              {t('past')} ({pastAppointments.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming">
            {upcomingAppointments.length === 0 ? (
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-12 text-center">
                  <Calendar className="w-16 h-16 mx-auto mb-4 text-slate-600" />
                  <h3 className="text-xl font-semibold mb-2">{t('no_upcoming_appointments')}</h3>
                  <p className="text-slate-400 mb-4">{t('schedule_meeting_desc')}</p>
                  <Button onClick={() => setCreateDialogOpen(true)} className="gap-2">
                    <Plus className="w-4 h-4" />
                    {t('create_appointment')}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {upcomingAppointments.map((appt, idx) => (
                  <AppointmentCard 
                    key={appt.appointment_id}
                    appointment={appt}
                    onView={() => { setSelectedAppointment(appt); setViewDialogOpen(true); }}
                    onRespond={handleRespond}
                    delay={idx * 0.05}
                    t={t}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="past">
            {pastAppointments.length === 0 ? (
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-12 text-center text-slate-400">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No past appointments</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pastAppointments.map((appt, idx) => (
                  <AppointmentCard 
                    key={appt.appointment_id}
                    appointment={appt}
                    onView={() => { setSelectedAppointment(appt); setViewDialogOpen(true); }}
                    isPast
                    delay={idx * 0.05}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Appointment Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-amber-500" />
              {t('new_appointment')}
            </DialogTitle>
            <DialogDescription>{t('schedule_meeting_members')}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Title */}
            <div className="space-y-2">
              <Label>{t('title')} *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={t('meeting_title_placeholder')}
                className="bg-slate-800 border-slate-700"
              />
            </div>

            {/* Type & Location */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('type')}</Label>
                <Select 
                  value={formData.appointment_type} 
                  onValueChange={(v) => setFormData({ ...formData, appointment_type: v })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {APPOINTMENT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {t(`appt_type_${type.value}`) || type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('location_type')}</Label>
                <Select 
                  value={formData.location_type} 
                  onValueChange={(v) => setFormData({ ...formData, location_type: v })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCATION_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {t(`loc_type_${type.value}`) || type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('date')} *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Start Time *</Label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Duration (min)</Label>
                <Input
                  type="number"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label>Location / Meeting Link</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder={formData.location_type === 'virtual' ? 'https://zoom.us/...' : 'Office, Room 101...'}
                className="bg-slate-800 border-slate-700"
              />
            </div>

            {/* Invitees */}
            <div className="space-y-2">
              <Label>Invite Members</Label>
              <Select 
                onValueChange={(v) => {
                  if (!formData.invitees.includes(v)) {
                    setFormData({ ...formData, invitees: [...formData.invitees, v] });
                  }
                }}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue placeholder="Select members to invite..." />
                </SelectTrigger>
                <SelectContent>
                  {members.map(member => (
                    <SelectItem key={member.user_id} value={member.user_id}>
                      {member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.invitees.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.invitees.map(userId => {
                    const member = members.find(m => m.user_id === userId);
                    return (
                      <Badge key={userId} variant="secondary" className="gap-1">
                        {member?.name || userId}
                        <X 
                          className="w-3 h-3 cursor-pointer" 
                          onClick={() => setFormData({
                            ...formData,
                            invitees: formData.invitees.filter(id => id !== userId)
                          })}
                        />
                      </Badge>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Guest Info */}
            <div className="space-y-2">
              <Label>Or Invite Guest (non-member)</Label>
              <div className="grid grid-cols-3 gap-2">
                <Input
                  value={formData.guest_name}
                  onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                  placeholder="Guest name"
                  className="bg-slate-800 border-slate-700"
                />
                <Input
                  type="email"
                  value={formData.guest_email}
                  onChange={(e) => setFormData({ ...formData, guest_email: e.target.value })}
                  placeholder="Guest email"
                  className="bg-slate-800 border-slate-700"
                />
                <Input
                  value={formData.guest_phone}
                  onChange={(e) => setFormData({ ...formData, guest_phone: e.target.value })}
                  placeholder="Guest phone"
                  className="bg-slate-800 border-slate-700"
                />
              </div>
            </div>

            {/* Description & Notes */}
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Meeting agenda, topics to discuss..."
                className="bg-slate-800 border-slate-700"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleCreate}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Appointment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Appointment Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
          {selectedAppointment && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedAppointment.title}</DialogTitle>
                <DialogDescription>
                  <Badge className={STATUS_COLORS[selectedAppointment.status]}>
                    {selectedAppointment.status}
                  </Badge>
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-slate-400" />
                  <span>{formatDate(selectedAppointment.date)} at {selectedAppointment.start_time}</span>
                </div>
                
                {selectedAppointment.location && (
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-slate-400" />
                    <span>{selectedAppointment.location}</span>
                  </div>
                )}

                {selectedAppointment.description && (
                  <div className="p-3 bg-slate-800 rounded-lg">
                    <p className="text-sm">{selectedAppointment.description}</p>
                  </div>
                )}

                {/* Responses */}
                {selectedAppointment.responses?.length > 0 && (
                  <div>
                    <Label className="mb-2">Responses</Label>
                    <div className="space-y-2">
                      {selectedAppointment.responses.map(resp => {
                        const respConfig = RESPONSE_ICONS[resp.response];
                        const Icon = respConfig?.icon || HelpCircle;
                        return (
                          <div key={resp.response_id} className="flex items-center gap-2 text-sm">
                            <Icon className={`w-4 h-4 ${respConfig?.color}`} />
                            <span>{resp.user_id}</span>
                            <span className="text-slate-400">- {resp.response}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                {selectedAppointment.my_response === undefined && (
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="text-green-500"
                      onClick={() => handleRespond(selectedAppointment.appointment_id, 'accepted')}
                    >
                      <Check className="w-4 h-4 mr-1" /> Accept
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="text-red-500"
                      onClick={() => handleRespond(selectedAppointment.appointment_id, 'declined')}
                    >
                      <X className="w-4 h-4 mr-1" /> Decline
                    </Button>
                  </div>
                )}
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => handleDelete(selectedAppointment.appointment_id)}
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Appointment Card Component
const AppointmentCard = ({ appointment, onView, onRespond, isPast, delay = 0 }) => {
  const TypeIcon = APPOINTMENT_TYPES.find(t => t.value === appointment.appointment_type)?.icon || Calendar;
  const LocationIcon = LOCATION_TYPES.find(t => t.value === appointment.location_type)?.icon || Building;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
    >
      <Card 
        className={`bg-slate-900 border-slate-800 hover:border-slate-700 transition-colors cursor-pointer ${isPast ? 'opacity-60' : ''}`}
        onClick={onView}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
              <TypeIcon className="w-6 h-6 text-purple-400" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold truncate">{appointment.title}</h3>
                <Badge className={STATUS_COLORS[appointment.status]} variant="outline">
                  {appointment.status}
                </Badge>
              </div>
              
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(appointment.date + 'T00:00:00').toLocaleDateString()}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {appointment.start_time}
                </span>
                {appointment.location && (
                  <span className="flex items-center gap-1">
                    <LocationIcon className="w-4 h-4" />
                    {appointment.location}
                  </span>
                )}
              </div>
              
              {appointment.creator_name && (
                <p className="text-xs text-slate-500 mt-2">
                  Created by {appointment.creator_name}
                </p>
              )}
            </div>

            {!isPast && appointment.my_response === undefined && (
              <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                <Button 
                  size="sm" 
                  variant="ghost"
                  className="text-green-500 h-8 w-8 p-0"
                  onClick={() => onRespond(appointment.appointment_id, 'accepted')}
                >
                  <Check className="w-4 h-4" />
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost"
                  className="text-red-500 h-8 w-8 p-0"
                  onClick={() => onRespond(appointment.appointment_id, 'declined')}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default AppointmentsPage;
